import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Compass, Heart } from "lucide-react";

const VALUES = [
  {
    icon: Target,
    title: "Missão",
    desc: "Transformar instituições de saúde através de tecnologia de alto padrão, dados confiáveis e inteligência aplicada.",
  },
  {
    icon: Compass,
    title: "Visão",
    desc: "Ser referência nacional em soluções digitais para hospitais e clínicas, elevando o padrão de cuidado através da tecnologia.",
  },
  {
    icon: Heart,
    title: "Valores",
    desc: "Excelência técnica, ética no tratamento de dados, senso de urgência clínica e parceria de longo prazo.",
  },
];

export default function About() {
  useEffect(() => {
    document.title = "Sobre — Áxis Saúde Digital";
  }, []);

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="bg-white pt-16 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
              Quem somos
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Tecnologia com{" "}
              <span className="text-[#1E3A8A]">propósito clínico.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl">
              A Áxis Saúde Digital nasceu da união entre especialistas em
              tecnologia e profissionais que vivem a rotina hospitalar. Somos
              consultores, engenheiros de dados e desenvolvedores dedicados
              exclusivamente ao setor de saúde.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div
                aria-hidden
                className="absolute -bottom-5 -left-5 w-full h-full bg-[#F9FAFB]"
              />
              <img
                src="https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMGhlYWx0aGNhcmV8ZW58MHx8fHwxNzgzNzMwMzI2fDA&ixlib=rb-4.1.0&q=85"
                alt="Profissional de saúde apresentando conteúdo em tablet"
                className="relative w-full h-80 lg:h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F9FAFB] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                data-testid={`value-card-${i}`}
                className="relative bg-white border border-gray-200 p-8 lg:p-10"
              >
                <span
                  aria-hidden
                  className="absolute top-0 left-0 h-[2px] w-16 bg-[#1E3A8A]"
                />
                <div className="w-12 h-12 bg-[#111827] text-white flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-2xl font-medium tracking-tight text-[#111827]">
                  {title}
                </h3>
                <p className="mt-3 text-base text-gray-600 leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="bg-[#0B1220] text-white py-24 lg:py-32 relative overflow-hidden">
        <div className="grain absolute inset-0" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
                Nossos números
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold leading-[1.05]">
                Resultado é o que{" "}
                <span className="text-[#1E3A8A]">nos define.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-gray-800">
            {[
              { n: "1", label: "Instituição atendida" },
              { n: "13+", label: "Soluções no portfólio" },
              { n: "99.9%", label: "SLA de disponibilidade" },
              { n: "1", label: "Ano de operação" },
            ].map((s, i) => (
              <div
                key={i}
                className="bg-[#0B1220] p-8 lg:p-10 hover:bg-[#111827] transition-colors"
              >
                <p className="font-display text-5xl lg:text-6xl font-semibold text-white tracking-tighter">
                  {s.n}
                </p>
                <p className="mt-3 text-sm text-gray-400 font-mono-axis uppercase tracking-widest">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-left">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05] max-w-3xl">
            Quer conhecer melhor nossa metodologia?
          </h2>
          <Link
            to="/contato"
            data-testid="about-cta"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
          >
            Fale com a equipe Áxis
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
